import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { map } from 'rxjs/operators';
import { IOption } from '../../../models/Models';
import { ScrollService } from '../../../services/scroll/scroll.service';

@Component({
  selector: 'ff-autocomplete-dropdown',
  templateUrl: './autocomplete-dropdown.component.html',
  styleUrls: ['./autocomplete-dropdown.component.scss'],
})
export class AutocompleteDropdownComponent implements OnInit {
  @ViewChild(MatAutocompleteTrigger) trigger: MatAutocompleteTrigger;

  /**
   * Text for dropdown placeholder
   * Default value: option
   * @internal
   */
  @Input() element: string = 'Option';

  /**
   * Options to display as radio buttons
   * Use the IOption interface: { value: string; text: string; selected?: boolean; }
   * @internal
   */
  @Input() options: IOption[] = [];

  private originalOptions: IOption[] = [];

  /**
   * The default selected value of the dropdown
   */
  @Input() set default(option: IOption) {
    if (this.selectedValue.value !== option?.value) {
      if (this.selectedValue.value === '') {
        this.formControl.setValue((this.selectedValue = option), {
          emitEvent: false,
        });
      } else {
        this.formControl.setValue((this.selectedValue = option), {
          emitEvent: false,
        });
        this.onSelectionChange();
      }
    }
  }

  /**
   * Emit the value selected by the user
   * @internal
   */
  @Output() elementSelected = new EventEmitter<IOption>();

  /**
   * Emits when the dropdown menu is opened
   */
  @Output() opened = new EventEmitter();

  /**
   * Emits when the dropdown menu is closed
   */
  @Output() closed = new EventEmitter();

  formControl = new FormControl();

  selectedValue: IOption = {
    text: '',
    value: '',
    selected: false,
  };

  constructor(private scrollService: ScrollService) {}

  /**
   * Callback to apply custom filtering on the option list.
   * If provided it must set the options input with the filter result.
   * By default if filters all options where the display text starts with the input, case insensitive.
   * @param input Filtering value provided by the user
   */
  @Input() filter: (input: string) => void = (input: string) => {
    input = input.toLocaleLowerCase();
    this.options = this.originalOptions.filter((option) =>
      option.text.toLocaleLowerCase().startsWith(input)
    );
  };

  ngOnInit(): void {
    this.originalOptions = [...this.options];
    this.formControl.valueChanges
      .pipe(map((value) => (typeof value === 'string' ? value : value.text)))
      .subscribe((value) => this.filter(value));
    this.scrollService.scrollEvent.subscribe(() => {
      this.trigger.closePanel();
    });
  }

  display(option: IOption) {
    return option?.text ?? '';
  }

  onOpened(): void {
    const value = this.formControl.value ?? '';
    this.opened.emit(typeof value === 'string' ? value : value.text);
  }

  onClosed(): void {
    const value = this.formControl.value ?? '';
    this.closed.emit(typeof value === 'string' ? value : value.text);
  }

  onSelectionChange(): void {
    this.elementSelected.emit((this.selectedValue = this.formControl.value));
  }
}
